import { useState, useEffect } from "react";
import axios from "axios";
import { Users, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { B } from "../clients/record/BillingCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;
const FOCUS = "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD108]";

const KINDS = [
  ["office", "Office"],
  ["mobile", "Mobile"],
  ["timesheet", "Timesheet"],
  ["guardian", "Guardian"],
];

const amount = (m) => `${m.currency} ${Number(m.amount).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* One client's lines, opened on demand: how many of each thing, at what each,
   adding up to what Stripe charges. */
const Lines = ({ row }) => (
  <div className="px-3.5 pb-2.5" data-testid={`seats-lines-${row.client_id}`}>
    {row.included && (
      <div className="text-[11.5px] mb-1" style={{ color: B.ink3 }}>
        Plan includes {row.included.office} office and {row.included.mobile} mobile
        user{row.included.mobile === 1 ? "" : "s"} before any extras.
      </div>
    )}
    {row.lines.map((ln, i) => (
      <div key={i} className="flex items-baseline gap-2 py-0.5 text-[12px]">
        <span className="flex-1 min-w-0 truncate" style={{ color: B.ink }}>{ln.label}</span>
        <span className="mono text-right w-[34px] shrink-0" style={{ color: B.ink2 }}>×{ln.quantity}</span>
        <span className="mono text-right w-[82px] shrink-0 truncate" style={{ color: B.ink3 }}>{amount(ln.unit)}</span>
        <span className="mono text-right w-[86px] shrink-0 truncate"
          style={{ color: ln.unverified ? B.bad : B.ink }}>
          {ln.unverified ? "not confirmed" : amount(ln.total)}
        </span>
      </div>
    ))}
    {row.unrecognised?.length > 0 && (
      <div className="text-[11.5px] mt-1" style={{ color: B.bad }}>
        Not counted as users: {row.unrecognised.join(", ")} — tell me what these are and
        I'll classify them.
      </div>
    )}
  </div>
);

/* Who pays for how many users, side by side. Currencies are never added
   together: one client bills in GBP and a blended total would be true of nobody. */
export function SeatsPanel() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/seats`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, []);

  if (!d) return null;
  if (d.error) {
    return <div className="text-[12.5px]" style={{ color: B.ink2 }} data-testid="seats-error">
      User numbers could not be loaded.
    </div>;
  }

  const rows = d.clients || [];
  return (
    <div className="rounded-[11px] overflow-hidden" data-testid="billing-seats-panel"
      style={{ background: B.card, border: `1px solid ${B.border}` }}>
      <div className="px-3.5 py-2.5 flex items-center gap-2 flex-wrap"
        style={{ borderBottom: `1px solid ${B.divider}` }}>
        <Users className="w-4 h-4" style={{ color: B.ink3 }} />
        <span className="text-[13.5px] font-bold" style={{ color: B.ink }}>User numbers</span>
        <span className="text-[12px]" style={{ color: B.ink3 }}>
          {d.total_users} across {d.client_count} paying client{d.client_count === 1 ? "" : "s"}
        </span>
        <span className="ml-auto mono text-[12.5px]" style={{ color: B.ink2 }}
          data-testid="seats-billed-total">
          {(d.billed || []).map((m) => amount(m)).join(" + ")} per month
        </span>
      </div>

      <div className="px-3.5 py-2 flex items-center gap-4 flex-wrap"
        style={{ borderBottom: `1px solid ${B.divider}`, background: B.panel }}>
        {KINDS.map(([k, label]) => (
          <span key={k} className="text-[12px]" style={{ color: B.ink2 }} data-testid={`seats-total-${k}`}>
            <span className="mono font-bold" style={{ color: B.ink }}>{d.users?.[k] ?? 0}</span> {label}
          </span>
        ))}
        <span className="text-[11.5px] ml-auto" style={{ color: B.ink3 }}>
          A plan includes {d.included_per_plan?.office} office and {d.included_per_plan?.mobile} mobile
        </span>
      </div>

      {d.unrecognised?.length > 0 && (
        <div className="px-3.5 py-2 text-[12px]"
          style={{ background: B.panel, color: B.ink2, borderBottom: `1px solid ${B.divider}` }}
          data-testid="seats-unrecognised">
          Not counted as users: <span className="font-semibold">{d.unrecognised.join(", ")}</span> —
          a client paying only for these shows zero users. Tell me what they are and I'll count them.
        </div>
      )}

      {d.unlinked_subscriptions > 0 && (
        <div className="px-3.5 py-2 text-[12px] flex items-start gap-1.5"
          style={{ background: "#FFF8D6", color: "#8A6D00", borderBottom: `1px solid ${B.divider}` }}
          data-testid="seats-unlinked">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {d.unlinked_subscriptions} active subscription{d.unlinked_subscriptions === 1 ? " is" : "s are"} not
          linked to a client, so their users are not counted here.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="px-3.5 py-8 text-[13px]" style={{ color: B.ink2 }} data-testid="seats-empty">
          No client has a running subscription in this Stripe book.
        </div>
      ) : rows.map((r) => {
        const isOpen = open === r.client_id;
        return (
          <div key={r.client_id} style={{ borderBottom: `1px solid ${B.divider}` }}>
            <button onClick={() => setOpen(isOpen ? null : r.client_id)}
              data-testid={`seats-row-${r.client_id}`}
              className={`w-full text-left px-3.5 py-2 flex items-center gap-2 hover:bg-[#FBFAF7] ${FOCUS}`}>
              {isOpen ? <ChevronDown className="w-3.5 h-3.5 shrink-0" style={{ color: B.ink3 }} />
                : <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: B.ink3 }} />}
              <span className="flex-1 min-w-0 truncate text-[13px] font-semibold" style={{ color: B.ink }}>
                {r.company_name}
                {r.trialing && <span className="ml-1.5 text-[10px] font-bold uppercase"
                  style={{ color: B.accentInk }}>trial</span>}
              </span>
              <span className="mono text-[14px] font-bold text-right w-[34px] shrink-0" style={{ color: B.ink }}>
                {r.total_users}
              </span>
              <span className="hidden sm:block text-[11.5px] w-[150px] shrink-0 truncate"
                style={{ color: B.ink3 }}>
                {r.total_users === 0 && r.unrecognised?.length > 0
                  ? `${r.unrecognised.length} product${r.unrecognised.length === 1 ? "" : "s"} not recognised`
                  : KINDS.filter(([k]) => r.users?.[k])
                    .map(([k, l]) => `${r.users[k]} ${l.toLowerCase()}`).join(" · ")}
              </span>
              <span className="mono text-[12px] sm:text-[12.5px] text-right shrink-0 w-[108px] truncate"
                style={{ color: r.figures_unverified ? B.bad : B.ink2 }}
                data-testid={`seats-money-${r.client_id}`}>
                {r.figures_unverified
                  ? `${(r.charge_currencies || []).join("/")} not confirmed`
                  : (r.totals || []).map((m) => amount(m)).join(" + ")}
              </span>
            </button>
            {isOpen && <Lines row={r} />}
          </div>
        );
      })}
    </div>
  );
}
